import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Bot,
  Plus,
  Play,
  Pause,
  Save,
  Settings,
  Zap,
  Globe,
  CreditCard,
  Receipt,
  DollarSign,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Trash2,
  Edit,
  Eye,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  MousePointer,
  Type,
  List,
  Download,
  Clock,
  ArrowLeft,
  Wand2,
  RefreshCw,
  Target,
  Sparkles,
  GripVertical,
  FlaskConical,
  Image,
  ExternalLink,
  History
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STEP_ICONS = {
  navigate: Globe,
  click: MousePointer,
  fill: Type,
  select: List,
  wait: Clock,
  download: Download,
  loop: RefreshCw,
  ai_decision: Sparkles,
  extract: Target,
};

const ROLE_CONFIG = {
  bill_collector: {
    label: "Bill Collector",
    icon: Receipt,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    description: "Navigate to billing section, find accounts, download bills"
  },
  payment_setup: {
    label: "Payment Setup",
    icon: CreditCard,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    description: "Navigate to payment methods, add bank/card details"
  },
  pay_agent: {
    label: "Pay Agent",
    icon: DollarSign,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    description: "Select bill, choose payment method, submit payment"
  }
};

function AgentStudio() {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const iframeRef = useRef(null);
  
  // State
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeWorkflow, setActiveWorkflow] = useState("bill_collector");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Training state
  const [trainingMode, setTrainingMode] = useState("ai_assisted"); // manual, ai_assisted, full_auto
  const [trainingSession, setTrainingSession] = useState(null);
  const [isTraining, setIsTraining] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Browser session state
  const [browserSession, setBrowserSession] = useState(null);
  const [browserScreenshot, setBrowserScreenshot] = useState(null);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [clickPosition, setClickPosition] = useState(null);
  const screenshotRef = useRef(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [showTestResultsDialog, setShowTestResultsDialog] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  
  // Test suite state
  const [isRunningTest, setIsRunningTest] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [selectedTestWorkflow, setSelectedTestWorkflow] = useState(null); // null = run all
  
  // Credentials form state
  const [credentialsForm, setCredentialsForm] = useState({
    test_username: "",
    test_password: "",
    portal_url: ""
  });
  
  // Form states
  const [newAgentForm, setNewAgentForm] = useState({
    name: "",
    portal_name: "",
    portal_url: "",
    category: "utility",
    description: "",
    capabilities: ["bill_collector"],
    test_username: "",
    test_password: ""
  });
  
  const [stepForm, setStepForm] = useState({
    action_type: "click",
    description: "",
    selector: "",
    selector_fallback: "",
    value: "",
    wait_after: 1000,
    timeout: 30000,
    is_optional: false,
    on_failure: "stop"
  });

  // Load agents
  useEffect(() => {
    loadAgents();
  }, []);

  // Load specific agent if agentId provided
  useEffect(() => {
    if (agentId && agents.length > 0) {
      const agent = agents.find(a => a.agent_id === agentId);
      if (agent) {
        setSelectedAgent(agent);
      }
    }
  }, [agentId, agents]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await api.get("/studio/agents");
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error("Failed to load agents:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Agent Studio requires bot_creator role or higher.");
        navigate("/");
      } else {
        toast.error("Failed to load agents");
      }
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async () => {
    try {
      setSaving(true);
      const response = await api.post("/studio/agents", newAgentForm);
      toast.success("Master Agent created!");
      setShowCreateDialog(false);
      setNewAgentForm({
        name: "",
        portal_name: "",
        portal_url: "",
        category: "utility",
        description: "",
        capabilities: ["bill_collector"],
        test_username: "",
        test_password: ""
      });
      loadAgents();
      // Select the newly created agent
      if (response.data.agent) {
        setSelectedAgent(response.data.agent);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create agent");
    } finally {
      setSaving(false);
    }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm("Are you sure you want to delete this Master Agent?")) return;
    
    try {
      await api.delete(`/studio/agents/${agentId}`);
      toast.success("Agent deleted");
      if (selectedAgent?.agent_id === agentId) {
        setSelectedAgent(null);
      }
      loadAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete agent");
    }
  };

  const openCredentialsDialog = () => {
    if (selectedAgent) {
      setCredentialsForm({
        test_username: selectedAgent.test_credentials?.username || "",
        test_password: selectedAgent.test_credentials?.password || "",
        portal_url: selectedAgent.portal_url || ""
      });
      setShowCredentialsDialog(true);
    }
  };

  const updateCredentials = async () => {
    if (!selectedAgent) return;
    
    try {
      setSaving(true);
      await api.put(`/studio/agents/${selectedAgent.agent_id}/credentials`, credentialsForm);
      toast.success("Test credentials updated!");
      setShowCredentialsDialog(false);
      // Reload agent
      const response = await api.get(`/studio/agents/${selectedAgent.agent_id}`);
      setSelectedAgent(response.data);
      loadAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update credentials");
    } finally {
      setSaving(false);
    }
  };

  const startTraining = async () => {
    if (!selectedAgent) return;
    
    try {
      setBrowserLoading(true);
      
      // Start training session
      const sessionResponse = await api.post(`/studio/agents/${selectedAgent.agent_id}/training/start`, null, {
        params: {
          workflow_role: activeWorkflow,
          mode: trainingMode
        }
      });
      setTrainingSession(sessionResponse.data.session);
      
      // Start browser session
      const browserResponse = await api.post('/studio/browser/start', {
        start_url: selectedAgent.portal_url,
        username: selectedAgent.test_credentials?.username || '',
        password: selectedAgent.test_credentials?.password || ''
      }, {
        params: { session_id: sessionResponse.data.session.session_id }
      });
      
      setBrowserSession(browserResponse.data);
      setBrowserScreenshot(browserResponse.data.screenshot);
      setCurrentUrl(browserResponse.data.current_url);
      setIsTraining(true);
      toast.success("Training session started with live browser!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start training");
    } finally {
      setBrowserLoading(false);
    }
  };

  const stopTraining = async () => {
    if (!trainingSession) return;
    
    try {
      // Stop browser session first
      if (browserSession) {
        try {
          const stopResponse = await api.post(`/studio/browser/${trainingSession.session_id}/stop`);
          // Get recorded steps from browser session
          if (stopResponse.data.recorded_steps?.length > 0) {
            toast.info(`${stopResponse.data.recorded_steps.length} steps recorded from browser`);
          }
        } catch (e) {
          console.warn("Browser session may already be closed");
        }
      }
      
      await api.post(`/studio/training/${trainingSession.session_id}/complete`);
      toast.success("Training completed and workflow saved!");
      setIsTraining(false);
      setTrainingSession(null);
      setBrowserSession(null);
      setBrowserScreenshot(null);
      // Reload agent to get updated workflow
      const response = await api.get(`/studio/agents/${selectedAgent.agent_id}`);
      setSelectedAgent(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete training");
    }
  };

  const refreshBrowser = async () => {
    if (!browserSession || !trainingSession) return;
    
    try {
      setBrowserLoading(true);
      const response = await api.get(`/studio/browser/${trainingSession.session_id}`);
      setBrowserScreenshot(response.data.screenshot);
      setCurrentUrl(response.data.current_url);
    } catch (error) {
      toast.error("Failed to refresh browser");
    } finally {
      setBrowserLoading(false);
    }
  };

  const navigateBrowser = async () => {
    if (!browserSession || !trainingSession || !currentUrl) return;
    
    try {
      setBrowserLoading(true);
      const response = await api.post(`/studio/browser/${trainingSession.session_id}/navigate`, {
        url: currentUrl
      });
      
      if (response.data.success) {
        setBrowserScreenshot(response.data.screenshot);
        setCurrentUrl(response.data.current_url);
        toast.success("Navigated successfully");
      } else {
        toast.error(response.data.error || "Navigation failed");
      }
    } catch (error) {
      toast.error("Failed to navigate");
    } finally {
      setBrowserLoading(false);
    }
  };

  const scrollBrowser = async (direction) => {
    if (!browserSession || !trainingSession) return;
    
    try {
      setBrowserLoading(true);
      const response = await api.post(`/studio/browser/${trainingSession.session_id}/scroll`, {
        direction: direction,
        amount: 400
      });
      
      if (response.data.success) {
        setBrowserScreenshot(response.data.screenshot);
      }
    } catch (error) {
      toast.error("Failed to scroll");
    } finally {
      setBrowserLoading(false);
    }
  };

  const handleBrowserClick = async (e) => {
    if (!browserSession || !trainingSession || !screenshotRef.current) return;
    
    const rect = screenshotRef.current.getBoundingClientRect();
    const scaleX = 1280 / rect.width; // Original browser width
    const scaleY = 720 / rect.height; // Original browser height
    
    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);
    
    setClickPosition({ x, y });
    
    try {
      setBrowserLoading(true);
      const response = await api.post(`/studio/browser/${trainingSession.session_id}/click`, { x, y });
      
      if (response.data.success && response.data.step) {
        const step = response.data.step;
        
        // If it's an input field, show the step dialog to fill value
        if (step.action_type === 'fill') {
          setStepForm({
            ...stepForm,
            action_type: 'fill',
            selector: step.selector,
            description: step.description || `Fill ${step.element_info?.placeholder || 'input'}`,
          });
          setShowStepDialog(true);
        } else {
          // For clicks, add directly to training session
          await api.post(`/studio/training/${trainingSession.session_id}/record-step`, {
            action_type: step.action_type,
            description: step.description,
            selector: step.selector,
            wait_after: 1000
          });
          
          // Reload training session
          const sessionResponse = await api.get(`/studio/training/${trainingSession.session_id}`);
          setTrainingSession(sessionResponse.data);
          toast.success(`Step recorded: ${step.description}`);
        }
        
        // Update screenshot
        if (step.screenshot) {
          setBrowserScreenshot(step.screenshot);
        }
        if (step.current_url) {
          setCurrentUrl(step.current_url);
        }
      }
    } catch (error) {
      toast.error("Failed to record click");
    } finally {
      setBrowserLoading(false);
      setClickPosition(null);
    }
  };

  const executeBrowserAction = async (action) => {
    if (!browserSession || !trainingSession) return;
    
    try {
      setBrowserLoading(true);
      const response = await api.post(`/studio/browser/${trainingSession.session_id}/action`, action);
      
      if (response.data.success) {
        setBrowserScreenshot(response.data.screenshot);
        setCurrentUrl(response.data.current_url);
        toast.success("Action executed");
      } else {
        toast.error(response.data.error || "Action failed");
      }
    } catch (error) {
      toast.error("Failed to execute action");
    } finally {
      setBrowserLoading(false);
    }
  };

  const addStep = async () => {
    if (!selectedAgent) return;
    
    try {
      setSaving(true);
      
      if (trainingSession) {
        // Add to training session
        await api.post(`/studio/training/${trainingSession.session_id}/record-step`, stepForm);
        // Reload session
        const response = await api.get(`/studio/training/${trainingSession.session_id}`);
        setTrainingSession(response.data);
      } else {
        // Add directly to workflow
        await api.post(`/studio/agents/${selectedAgent.agent_id}/workflows/${activeWorkflow}/steps`, stepForm);
        // Reload agent
        const response = await api.get(`/studio/agents/${selectedAgent.agent_id}`);
        setSelectedAgent(response.data);
      }
      
      toast.success("Step added!");
      setShowStepDialog(false);
      resetStepForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add step");
    } finally {
      setSaving(false);
    }
  };

  const deleteStep = async (stepId) => {
    if (!selectedAgent) return;
    
    try {
      // If in training session, delete from session
      if (trainingSession) {
        // Remove step from training session's recorded_steps
        const updatedSteps = trainingSession.recorded_steps.filter(s => s.step_id !== stepId);
        setTrainingSession({
          ...trainingSession,
          recorded_steps: updatedSteps
        });
        toast.success("Step removed from training session");
      } else {
        // Delete from saved workflow
        await api.delete(`/studio/agents/${selectedAgent.agent_id}/workflows/${activeWorkflow}/steps/${stepId}`);
        // Reload agent
        const response = await api.get(`/studio/agents/${selectedAgent.agent_id}`);
        setSelectedAgent(response.data);
        toast.success("Step deleted");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete step");
    }
  };

  const resetStepForm = () => {
    setStepForm({
      action_type: "click",
      description: "",
      selector: "",
      selector_fallback: "",
      value: "",
      wait_after: 1000,
      timeout: 30000,
      is_optional: false,
      on_failure: "stop"
    });
    setEditingStep(null);
  };

  const getAiSuggestion = async () => {
    // This would capture a screenshot and send to AI
    // For now, show a placeholder
    setAiLoading(true);
    toast.info("AI analysis requires a live browser connection. This feature is coming soon!");
    setTimeout(() => setAiLoading(false), 1000);
  };

  // ============ TEST SUITE FUNCTIONS ============
  const runAgentTest = async (workflow = null) => {
    if (!selectedAgent) return;
    
    try {
      setIsRunningTest(true);
      setTestResults(null);
      setSelectedTestWorkflow(workflow);
      setShowTestResultsDialog(true);
      
      toast.info(`Running ${workflow ? ROLE_CONFIG[workflow]?.label : 'full test suite'}...`);
      
      const response = await api.post(`/studio/agents/${selectedAgent.agent_id}/test`, {
        workflow: workflow
      });
      
      setTestResults(response.data);
      
      // Refresh test history after run
      loadTestHistory();
      
      if (response.data.overall_status === 'passed') {
        toast.success('All tests passed!');
      } else if (response.data.overall_status === 'partial') {
        toast.warning('Some tests had issues');
      } else {
        toast.error('Tests failed');
      }
    } catch (error) {
      console.error('Test failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to run tests';
      toast.error(errorMessage);
      
      // Show error in dialog instead of closing
      setTestResults({
        overall_status: 'failed',
        error_message: errorMessage,
        tests: {}
      });
    } finally {
      setIsRunningTest(false);
    }
  };

  const viewTestResult = async (testRunId) => {
    try {
      const response = await api.get(`/studio/agents/${selectedAgent.agent_id}/test-results/${testRunId}`);
      setTestResults(response.data);
      setShowTestResultsDialog(true);
    } catch (error) {
      toast.error('Failed to load test result');
    }
  };

  const loadTestHistory = async () => {
    if (!selectedAgent) return;
    
    try {
      const response = await api.get(`/studio/agents/${selectedAgent.agent_id}/test-results?limit=10`);
      setTestHistory(response.data.results || []);
    } catch (error) {
      console.error('Failed to load test history:', error);
    }
  };

  // Load test history when agent is selected
  useEffect(() => {
    if (selectedAgent) {
      loadTestHistory();
    }
  }, [selectedAgent?.agent_id]);

  const currentWorkflow = selectedAgent?.workflows?.[activeWorkflow];
  const workflowSteps = trainingSession?.recorded_steps || currentWorkflow?.steps || [];

  // Render agent list
  const renderAgentList = () => (
    <div className="w-80 border-r border-zinc-800 flex flex-col h-full">
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Master Agents</h2>
          <Button
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>
        <Input
          placeholder="Search agents..."
          className="bg-zinc-900 border-zinc-700 text-white"
        />
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {agents.map((agent) => (
            <Card
              key={agent.agent_id}
              className={`cursor-pointer transition-all ${
                selectedAgent?.agent_id === agent.agent_id
                  ? "bg-blue-900/30 border-blue-500"
                  : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
              }`}
              onClick={() => setSelectedAgent(agent)}
            >
              <CardContent className="p-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white text-sm">{agent.name}</h3>
                      <p className="text-xs text-zinc-500">{agent.portal_name}</p>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      agent.status === "deployed"
                        ? "border-green-500 text-green-400"
                        : agent.status === "testing"
                        ? "border-yellow-500 text-yellow-400"
                        : "border-zinc-600 text-zinc-400"
                    }`}
                  >
                    {agent.status}
                  </Badge>
                </div>
                <div className="mt-2 flex gap-1">
                  {Object.keys(agent.workflows || {}).map((role) => {
                    const config = ROLE_CONFIG[role];
                    const Icon = config?.icon || Bot;
                    return (
                      <div
                        key={role}
                        className={`w-6 h-6 rounded flex items-center justify-center ${config?.bgColor || "bg-zinc-800"}`}
                        title={config?.label || role}
                      >
                        <Icon className={`w-3 h-3 ${config?.color || "text-zinc-400"}`} />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
          
          {agents.length === 0 && !loading && (
            <div className="text-center py-8 text-zinc-500">
              <Bot className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No Master Agents yet</p>
              <p className="text-xs mt-1">Create your first agent to get started</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  // Render workflow builder
  const renderWorkflowBuilder = () => {
    if (!selectedAgent) {
      return (
        <div className="flex-1 flex items-center justify-center text-zinc-500">
          <div className="text-center">
            <Bot className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-medium mb-2">Select a Master Agent</h3>
            <p className="text-sm">Choose an agent from the list or create a new one</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Agent header */}
        <div className="p-4 border-b border-zinc-800 bg-zinc-900/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">{selectedAgent.name}</h2>
                <p className="text-sm text-zinc-400">{selectedAgent.portal_url}</p>
                {selectedAgent.test_credentials?.username ? (
                  <p className="text-xs text-green-400">
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    Credentials configured
                  </p>
                ) : (
                  <p className="text-xs text-yellow-400">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    No credentials set
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Credentials Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={openCredentialsDialog}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Settings className="w-4 h-4 mr-2" />
                Credentials
              </Button>
              
              {isTraining ? (
                <>
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                    <span className="w-2 h-2 bg-red-400 rounded-full mr-2 animate-pulse" />
                    Training Active
                  </Badge>
                  <Button
                    variant="outline"
                    onClick={stopTraining}
                    className="border-green-500 text-green-400 hover:bg-green-500/10"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Training
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={startTraining}
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!selectedAgent.test_credentials?.username}
                    title={!selectedAgent.test_credentials?.username ? "Set credentials first" : ""}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Training
                  </Button>
                  {/* Run Test Button */}
                  <Button
                    variant="outline"
                    onClick={() => runAgentTest(null)}
                    disabled={!selectedAgent.test_credentials?.username || isRunningTest}
                    className="border-green-600 text-green-400 hover:bg-green-600/10"
                    title={!selectedAgent.test_credentials?.username ? "Set credentials first" : "Run automated tests"}
                  >
                    {isRunningTest ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FlaskConical className="w-4 h-4 mr-2" />
                    )}
                    Run Tests
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={() => deleteAgent(selectedAgent.agent_id)}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Workflow tabs */}
        <Tabs value={activeWorkflow} onValueChange={setActiveWorkflow} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 pt-4 border-b border-zinc-800">
            <TabsList className="bg-zinc-900">
              {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                const hasWorkflow = selectedAgent.workflows?.[role];
                const Icon = config.icon;
                return (
                  <TabsTrigger
                    key={role}
                    value={role}
                    className="data-[state=active]:bg-zinc-800"
                    disabled={!hasWorkflow}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${config.color}`} />
                    {config.label}
                    {hasWorkflow && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {hasWorkflow.steps?.length || 0}
                      </Badge>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Steps panel */}
            <div className="w-96 border-r border-zinc-800 flex flex-col">
              <div className="p-4 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-white">Workflow Steps</h3>
                    <p className="text-xs text-zinc-500">{ROLE_CONFIG[activeWorkflow]?.description}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowStepDialog(true)}
                    className="bg-zinc-800 hover:bg-zinc-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
                
                {/* Training mode selector */}
                <div className="mt-3 p-2 bg-zinc-900 rounded-lg">
                  <Label className="text-xs text-zinc-400 mb-2 block">Training Mode</Label>
                  <Select value={trainingMode} onValueChange={setTrainingMode}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white text-xs h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="manual">Manual (I click, bot records)</SelectItem>
                      <SelectItem value="ai_assisted">AI-Assisted (AI suggests, I confirm)</SelectItem>
                      <SelectItem value="full_auto">Full Auto (AI figures it out)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-4 space-y-2">
                  {workflowSteps.map((step, index) => {
                    const StepIcon = STEP_ICONS[step.action_type] || Target;
                    return (
                      <Card
                        key={step.step_id || index}
                        className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all"
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-4 h-4 text-zinc-600 cursor-grab" />
                              <div className="w-6 h-6 rounded bg-zinc-800 flex items-center justify-center text-xs text-zinc-400">
                                {index + 1}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <StepIcon className="w-4 h-4 text-blue-400" />
                                <span className="text-sm font-medium text-white capitalize">
                                  {step.action_type.replace("_", " ")}
                                </span>
                                {step.ai_generated && (
                                  <Sparkles className="w-3 h-3 text-purple-400" />
                                )}
                              </div>
                              <p className="text-xs text-zinc-400 truncate">{step.description}</p>
                              {step.selector && (
                                <code className="text-xs text-zinc-600 block mt-1 truncate">
                                  {step.selector}
                                </code>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 text-zinc-500 hover:text-white"
                                onClick={() => {
                                  setStepForm(step);
                                  setEditingStep(step);
                                  setShowStepDialog(true);
                                }}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 text-zinc-500 hover:text-red-400"
                                onClick={() => deleteStep(step.step_id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {workflowSteps.length === 0 && (
                    <div className="text-center py-8 text-zinc-500">
                      <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No steps recorded yet</p>
                      <p className="text-xs mt-1">Start training or add steps manually</p>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* AI Suggestions */}
              {trainingMode !== "manual" && (
                <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      <span className="text-sm font-medium text-white">AI Suggestions</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={getAiSuggestion}
                      disabled={aiLoading}
                      className="h-7 text-xs"
                    >
                      {aiLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Wand2 className="w-3 h-3 mr-1" />
                      )}
                      Analyze Page
                    </Button>
                  </div>
                  {aiSuggestions.length > 0 ? (
                    <div className="space-y-2">
                      {aiSuggestions.map((suggestion, i) => (
                        <div key={i} className="p-2 bg-zinc-800 rounded text-xs text-zinc-300">
                          {suggestion.description}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-500">
                      Click "Analyze Page" to get AI suggestions for the next step
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Preview panel */}
            <div className="flex-1 flex flex-col bg-zinc-950">
              <div className="p-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2">
                <Input
                  value={currentUrl || selectedAgent.portal_url}
                  onChange={(e) => setCurrentUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isTraining) {
                      navigateBrowser();
                    }
                  }}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                  placeholder="Enter URL..."
                />
                <Button 
                  size="sm" 
                  variant="default"
                  className="h-8 bg-blue-600 hover:bg-blue-700"
                  onClick={navigateBrowser}
                  disabled={!isTraining || browserLoading || !currentUrl}
                >
                  Go
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8"
                  onClick={refreshBrowser}
                  disabled={!isTraining || browserLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${browserLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="flex-1 flex items-center justify-center bg-zinc-900/30 overflow-hidden relative">
                {browserLoading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                  </div>
                )}
                
                {isTraining && browserScreenshot ? (
                  <div className="relative w-full h-full flex items-center justify-center p-4">
                    {/* Scroll Up Button */}
                    <Button
                      size="icon"
                      variant="outline"
                      className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20 bg-zinc-900/80 border-zinc-700 hover:bg-zinc-800"
                      onClick={() => scrollBrowser('up')}
                      disabled={browserLoading}
                    >
                      <ChevronUp className="w-5 h-5" />
                    </Button>
                    
                    <img
                      ref={screenshotRef}
                      src={`data:image/jpeg;base64,${browserScreenshot}`}
                      alt="Browser Preview"
                      className="max-w-full max-h-full object-contain cursor-crosshair border border-zinc-700 rounded shadow-lg"
                      onClick={handleBrowserClick}
                      style={{ maxHeight: 'calc(100vh - 300px)' }}
                    />
                    
                    {/* Scroll Down Button */}
                    <Button
                      size="icon"
                      variant="outline"
                      className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20 bg-zinc-900/80 border-zinc-700 hover:bg-zinc-800"
                      onClick={() => scrollBrowser('down')}
                      disabled={browserLoading}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </Button>
                    
                    {clickPosition && (
                      <div 
                        className="absolute w-4 h-4 bg-blue-500 rounded-full animate-ping"
                        style={{ 
                          left: `${clickPosition.x}px`, 
                          top: `${clickPosition.y}px`,
                          transform: 'translate(-50%, -50%)'
                        }}
                      />
                    )}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-zinc-900/90 px-4 py-2 rounded-lg border border-zinc-700">
                      <p className="text-xs text-zinc-400">
                        <MousePointer className="w-3 h-3 inline mr-1" />
                        Click on elements to record steps | Use arrows to scroll
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-zinc-500">
                    <Globe className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <h3 className="text-lg font-medium mb-2">Browser Preview</h3>
                    <p className="text-sm max-w-md">
                      Live browser preview will be available when training is active.
                      This allows you to interact with the portal and record navigation steps.
                    </p>
                    {!isTraining && (
                      <Button
                        onClick={startTraining}
                        className="mt-4 bg-blue-600 hover:bg-blue-700"
                        disabled={browserLoading}
                      >
                        {browserLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Start Training Session
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Tabs>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="h-14 border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm flex items-center px-4 sticky top-0 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          className="mr-3 text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-semibold">Agent Studio</span>
        </div>
        <div className="flex-1" />
        <Badge variant="outline" className="border-yellow-500/30 text-yellow-400">
          Beta
        </Badge>
      </header>

      {/* Main content */}
      <div className="flex h-[calc(100vh-56px)]">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : (
          <>
            {renderAgentList()}
            {renderWorkflowBuilder()}
          </>
        )}
      </div>

      {/* Create Agent Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-400" />
              Create Master Agent
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agent Name</Label>
                <Input
                  placeholder="e.g., ConEd Agent"
                  value={newAgentForm.name}
                  onChange={(e) => setNewAgentForm({ ...newAgentForm, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Portal Name</Label>
                <Input
                  placeholder="e.g., ConEd"
                  value={newAgentForm.portal_name}
                  onChange={(e) => setNewAgentForm({ ...newAgentForm, portal_name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Portal URL</Label>
              <Input
                placeholder="https://portal.coned.com/login"
                value={newAgentForm.portal_url}
                onChange={(e) => setNewAgentForm({ ...newAgentForm, portal_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newAgentForm.category}
                  onValueChange={(v) => setNewAgentForm({ ...newAgentForm, category: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="utility">Utility</SelectItem>
                    <SelectItem value="telecom">Telecom</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Capabilities</Label>
                <div className="flex gap-2">
                  {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                    const isSelected = newAgentForm.capabilities.includes(role);
                    const Icon = config.icon;
                    return (
                      <Button
                        key={role}
                        size="sm"
                        variant={isSelected ? "default" : "outline"}
                        className={isSelected ? "bg-blue-600" : ""}
                        onClick={() => {
                          const caps = isSelected
                            ? newAgentForm.capabilities.filter(c => c !== role)
                            : [...newAgentForm.capabilities, role];
                          setNewAgentForm({ ...newAgentForm, capabilities: caps });
                        }}
                      >
                        <Icon className="w-4 h-4" />
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <Separator className="bg-zinc-800" />
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-zinc-400" />
                Test Credentials (for training)
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Username"
                  value={newAgentForm.test_username}
                  onChange={(e) => setNewAgentForm({ ...newAgentForm, test_username: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={newAgentForm.test_password}
                  onChange={(e) => setNewAgentForm({ ...newAgentForm, test_password: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={createAgent}
              disabled={!newAgentForm.name || !newAgentForm.portal_url || saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Step Dialog */}
      <Dialog open={showStepDialog} onOpenChange={(open) => {
        setShowStepDialog(open);
        if (!open) resetStepForm();
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              {editingStep ? "Edit Step" : "Add Step"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Action Type</Label>
                <Select
                  value={stepForm.action_type}
                  onValueChange={(v) => setStepForm({ ...stepForm, action_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="navigate">Navigate to URL</SelectItem>
                    <SelectItem value="click">Click Element</SelectItem>
                    <SelectItem value="fill">Fill Input</SelectItem>
                    <SelectItem value="select">Select Option</SelectItem>
                    <SelectItem value="wait">Wait</SelectItem>
                    <SelectItem value="scroll">Scroll</SelectItem>
                    <SelectItem value="download">Download File</SelectItem>
                    <SelectItem value="loop">Loop</SelectItem>
                    <SelectItem value="ai_decision">AI Decision</SelectItem>
                    <SelectItem value="extract">Extract Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>On Failure</Label>
                <Select
                  value={stepForm.on_failure}
                  onValueChange={(v) => setStepForm({ ...stepForm, on_failure: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="stop">Stop Workflow</SelectItem>
                    <SelectItem value="skip">Skip Step</SelectItem>
                    <SelectItem value="retry">Retry</SelectItem>
                    <SelectItem value="ai_recover">AI Recovery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="e.g., Click the login button"
                value={stepForm.description}
                onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>CSS Selector</Label>
              <Input
                placeholder="e.g., #login-btn or .submit-button"
                value={stepForm.selector}
                onChange={(e) => setStepForm({ ...stepForm, selector: e.target.value })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fallback Selector (optional)</Label>
              <Input
                placeholder="e.g., button[type='submit']"
                value={stepForm.selector_fallback}
                onChange={(e) => setStepForm({ ...stepForm, selector_fallback: e.target.value })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            
            {(stepForm.action_type === "fill" || stepForm.action_type === "select" || stepForm.action_type === "navigate") && (
              <div className="space-y-2">
                <Label>Value</Label>
                <Input
                  placeholder={stepForm.action_type === "navigate" ? "URL" : "Use {variable} for dynamic values"}
                  value={stepForm.value}
                  onChange={(e) => setStepForm({ ...stepForm, value: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
                <p className="text-xs text-zinc-500">
                  Use {"{username}"} or {"{password}"} for credential values
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Wait After (ms)</Label>
                <Input
                  type="number"
                  value={stepForm.wait_after}
                  onChange={(e) => setStepForm({ ...stepForm, wait_after: parseInt(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Timeout (ms)</Label>
                <Input
                  type="number"
                  value={stepForm.timeout}
                  onChange={(e) => setStepForm({ ...stepForm, timeout: parseInt(e.target.value) || 30000 })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={stepForm.is_optional}
                onCheckedChange={(c) => setStepForm({ ...stepForm, is_optional: c })}
              />
              <Label>Optional step (skip if element not found)</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowStepDialog(false);
              resetStepForm();
            }}>
              Cancel
            </Button>
            <Button
              onClick={addStep}
              disabled={!stepForm.description || saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingStep ? "Update Step" : "Add Step"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              Test Credentials
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-400">
              Enter the credentials to use for training this Master Agent. These will be used to login and navigate the portal.
            </p>
            
            <div className="space-y-2">
              <Label>Portal URL</Label>
              <Input
                placeholder="https://secure.comed.com/MyAccount/"
                value={credentialsForm.portal_url}
                onChange={(e) => setCredentialsForm({ ...credentialsForm, portal_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Username / Email</Label>
              <Input
                placeholder="user@example.com"
                value={credentialsForm.test_username}
                onChange={(e) => setCredentialsForm({ ...credentialsForm, test_username: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={credentialsForm.test_password}
                onChange={(e) => setCredentialsForm({ ...credentialsForm, test_password: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={updateCredentials}
              disabled={!credentialsForm.test_username || !credentialsForm.test_password || saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Results Dialog */}
      <Dialog open={showTestResultsDialog} onOpenChange={setShowTestResultsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-green-400" />
              Agent Test Results
              {testResults && (
                <Badge 
                  className={`ml-2 ${
                    testResults.overall_status === 'passed' 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : testResults.overall_status === 'partial'
                      ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}
                >
                  {testResults.overall_status === 'passed' ? (
                    <><CheckCircle className="w-3 h-3 mr-1" /> Passed</>
                  ) : testResults.overall_status === 'partial' ? (
                    <><AlertCircle className="w-3 h-3 mr-1" /> Partial</>
                  ) : (
                    <><XCircle className="w-3 h-3 mr-1" /> Failed</>
                  )}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            {isRunningTest ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-blue-400 mb-4" />
                <p className="text-zinc-400 text-lg">Running automated tests...</p>
                <p className="text-zinc-500 text-sm mt-2">
                  Testing {selectedTestWorkflow ? ROLE_CONFIG[selectedTestWorkflow]?.label : 'all workflows'}
                </p>
                
                {/* Show workflow steps being tested */}
                {selectedAgent?.workflows && (
                  <div className="mt-6 w-full max-w-md">
                    <p className="text-xs text-zinc-500 mb-3 text-center">Executing workflow steps:</p>
                    {Object.entries(selectedAgent.workflows).map(([key, wf]) => (
                      <div key={key} className="mb-3">
                        <div className="flex items-center gap-2 text-sm mb-2">
                          {ROLE_CONFIG[key]?.icon && (() => { const Icon = ROLE_CONFIG[key].icon; return <Icon className={`w-4 h-4 ${ROLE_CONFIG[key]?.color}`} />; })()}
                          <span className="font-medium">{ROLE_CONFIG[key]?.label || key}</span>
                          <span className="text-zinc-500">({wf.steps?.length || 0} steps)</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {wf.steps?.slice(0, 8).map((step, idx) => (
                            <div 
                              key={idx} 
                              className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-400 animate-pulse"
                              style={{ animationDelay: `${idx * 200}ms` }}
                            >
                              {step.description?.substring(0, 20) || `Step ${idx + 1}`}
                            </div>
                          ))}
                          {(wf.steps?.length || 0) > 8 && (
                            <span className="px-2 py-1 text-xs text-zinc-500">+{wf.steps.length - 8} more</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-zinc-600 text-xs mt-4">This may take 2-5 minutes</p>
              </div>
            ) : testResults?.error_message ? (
              <div className="text-center py-12">
                <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <p className="text-lg text-red-400 font-medium">Test Failed to Start</p>
                <p className="text-zinc-500 mt-2">{testResults.error_message}</p>
                <div className="mt-6 text-left bg-red-900/20 border border-red-500/30 rounded-lg p-4 max-w-md mx-auto">
                  <h4 className="text-sm font-medium text-red-400 mb-2">Possible Issues:</h4>
                  <ul className="text-xs text-zinc-400 space-y-1">
                    <li>• Test credentials may not be configured</li>
                    <li>• The target portal may be unavailable</li>
                    <li>• Workflow steps may need updating</li>
                  </ul>
                </div>
              </div>
            ) : testResults ? (
              <div className="space-y-6 py-4">
                {/* Test Summary */}
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(testResults.tests || {}).map(([workflow, result]) => {
                    const config = ROLE_CONFIG[workflow];
                    const Icon = config?.icon || Bot;
                    return (
                      <Card 
                        key={workflow}
                        className={`bg-zinc-800 border-zinc-700 ${
                          result.status === 'passed' 
                            ? 'border-l-4 border-l-green-500'
                            : result.status === 'partial'
                            ? 'border-l-4 border-l-yellow-500'
                            : 'border-l-4 border-l-red-500'
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-5 h-5 ${config?.color || 'text-zinc-400'}`} />
                            <span className="font-medium">{config?.label || workflow}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {result.status === 'passed' ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : result.status === 'partial' ? (
                              <AlertCircle className="w-4 h-4 text-yellow-400" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400" />
                            )}
                            <span className="text-sm text-zinc-400 capitalize">{result.status}</span>
                          </div>
                          {result.errors?.length > 0 && (
                            <p className="text-xs text-red-400 mt-2 truncate">
                              {result.errors[0]}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Screenshots for each test */}
                {Object.entries(testResults.tests || {}).map(([workflow, result]) => (
                  <div key={workflow} className="space-y-3">
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      {ROLE_CONFIG[workflow]?.icon && 
                        <span className={ROLE_CONFIG[workflow]?.color}>
                          {(() => { const Icon = ROLE_CONFIG[workflow].icon; return <Icon className="w-5 h-5" />; })()}
                        </span>
                      }
                      {ROLE_CONFIG[workflow]?.label || workflow} Screenshots
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {result.screenshots?.map((screenshot, idx) => (
                        <Card key={idx} className="bg-zinc-800 border-zinc-700 overflow-hidden">
                          <CardHeader className="p-3 pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Image className="w-4 h-4 text-zinc-400" />
                              {screenshot.stage}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {screenshot.description}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="p-3 pt-0">
                            {screenshot.s3_url ? (
                              <a 
                                href={screenshot.s3_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <div className="aspect-video bg-zinc-900 rounded border border-zinc-700 flex items-center justify-center hover:border-blue-500 transition-colors">
                                  <div className="text-center">
                                    <ExternalLink className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                                    <span className="text-xs text-zinc-400">View Screenshot</span>
                                  </div>
                                </div>
                              </a>
                            ) : screenshot.screenshot_preview ? (
                              <div className="aspect-video bg-zinc-900 rounded border border-zinc-700 flex items-center justify-center">
                                <div className="text-center">
                                  <Image className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                                  <span className="text-xs text-zinc-500">Screenshot captured</span>
                                </div>
                              </div>
                            ) : (
                              <div className="aspect-video bg-zinc-900 rounded border border-zinc-700 flex items-center justify-center">
                                <span className="text-xs text-zinc-600">No screenshot</span>
                              </div>
                            )}
                            {screenshot.url && (
                              <p className="text-xs text-zinc-500 mt-2 truncate" title={screenshot.url}>
                                {screenshot.url}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Errors */}
                    {result.errors?.length > 0 && (
                      <Card className="bg-red-900/20 border-red-500/30">
                        <CardContent className="p-3">
                          <h4 className="text-sm font-medium text-red-400 mb-2">Errors</h4>
                          <ul className="space-y-1">
                            {result.errors.map((error, idx) => (
                              <li key={idx} className="text-xs text-red-300">
                                {error}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                    
                    <Separator className="bg-zinc-800" />
                  </div>
                ))}

                {/* Test History */}
                {testHistory.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium flex items-center gap-2 mb-3">
                      <History className="w-5 h-5 text-zinc-400" />
                      Recent Test Runs
                    </h3>
                    <div className="space-y-2">
                      {testHistory.slice(0, 5).map((run) => (
                        <div 
                          key={run.test_run_id}
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded border border-zinc-700 hover:border-zinc-600 cursor-pointer transition-colors"
                          onClick={() => viewTestResult(run.test_run_id)}
                        >
                          <div className="flex items-center gap-3">
                            {run.overall_status === 'passed' ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : run.overall_status === 'partial' ? (
                              <AlertCircle className="w-4 h-4 text-yellow-400" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400" />
                            )}
                            <div>
                              <span className="text-sm text-zinc-300">
                                {new Date(run.started_at).toLocaleString()}
                              </span>
                              <div className="flex gap-2 mt-1">
                                {Object.entries(run.tests || {}).map(([wf, t]) => (
                                  <Badge 
                                    key={wf}
                                    variant="outline" 
                                    className={`text-xs ${
                                      t.status === 'passed' ? 'text-green-400 border-green-500/30' :
                                      t.status === 'partial' ? 'text-yellow-400 border-yellow-500/30' :
                                      'text-red-400 border-red-500/30'
                                    }`}
                                  >
                                    {ROLE_CONFIG[wf]?.label?.split(' ')[0] || wf}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                          <Eye className="w-4 h-4 text-zinc-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No test results yet</p>
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowTestResultsDialog(false)}>
              Close
            </Button>
            {testResults && (
              <Button
                onClick={() => runAgentTest(null)}
                disabled={isRunningTest}
                className="bg-green-600 hover:bg-green-700"
              >
                {isRunningTest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Run Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AgentStudio;
